import { isPressedKey, KeyboardKey } from './key-util';
import { makeTabbable } from './make-tabbable';

const FOCUS_FIRST_ELEMENT_TIMEOUT = 100;

/**
 * Traps focus between the given two elements:
 * - if `firstElement` is focused and user presses Tab and Shift key, it focuses the `lastElement`
 * - if `lastElement` is focused and user presses Tab and NOT Shift key, it focuses the `firstElement`.
 * @param firstElement
 * @param lastElement
 * @param wrapper If provided and focus is not inside the wrapper element, it focuses the `firstElement`
 */
export function trapFocusBetweenElements(
  firstElement: HTMLElement,
  lastElement: HTMLElement,
  wrapper?: Element,
) {
  makeTabbable(firstElement);
  makeTabbable(lastElement);

  if (!document.activeElement || !wrapper?.contains(document.activeElement)) {
    setTimeout(() => {
      firstElement.focus();
    }, FOCUS_FIRST_ELEMENT_TIMEOUT);
  }

  const handleKeydownFirstElement = (e: KeyboardEvent) => {
    if (isPressedKey(e, KeyboardKey['tab+shift'])) {
      e.preventDefault();
      lastElement.focus();
    }
  };

  const handleKeydownLastElement = (e: KeyboardEvent) => {
    if (isPressedKey(e, KeyboardKey['tab-shift'])) {
      e.preventDefault();
      firstElement.focus();
    }
  };

  firstElement.addEventListener('keydown', handleKeydownFirstElement);
  lastElement.addEventListener('keydown', handleKeydownLastElement);

  return {
    terminate: () => {
      firstElement.removeEventListener('keydown', handleKeydownFirstElement);
      lastElement.removeEventListener('keydown', handleKeydownLastElement);
    },
  };
}
